import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useSimulationStore } from '@/stores/simulationStore';
import { useClientScores } from '@/features/completeness/hooks/useClientScores';

export function SimulationPanel({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { startSimulation } = useSimulationStore();

  const [searchFilter, setSearchFilter] = useState('');
  const [selectedGln, setSelectedGln] = useState<string | null>(null);
  const [selectedName, setSelectedName] = useState<string | null>(null);

  const { data: clientData, isLoading, isError } = useClientScores(true);
  const clients = useMemo(() => clientData?.data ?? [], [clientData?.data]);

  const filteredClients = useMemo(() => {
    if (!searchFilter) return clients;
    const q = searchFilter.toLowerCase();
    return clients.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.glnNumber.toLowerCase().includes(q),
    );
  }, [clients, searchFilter]);

  const handleStart = () => {
    if (selectedGln && selectedName) {
      startSimulation(selectedGln, selectedName);
      onClose();
      navigate('/dashboard');
    }
  };

  return (
    <div
      data-testid="simulation-panel"
      role="dialog"
      aria-label={t('simulation.viewAs')}
      className="absolute right-0 top-10 w-72 rounded-lg border p-3 shadow-lg"
      style={{
        backgroundColor: 'var(--background)',
        borderColor: 'var(--border)',
        color: 'var(--foreground)',
      }}
    >
      <div className="mb-2 text-xs font-semibold uppercase" style={{ color: 'var(--foreground-muted)' }}>
        {t('simulation.viewAs')}
      </div>

      <label htmlFor="simulation-search" className="sr-only">
        {t('simulation.searchClient')}
      </label>
      <input
        id="simulation-search"
        data-testid="simulation-search"
        type="text"
        value={searchFilter}
        onChange={(e) => setSearchFilter(e.target.value)}
        placeholder={t('simulation.searchClient')}
        className="mb-2 w-full rounded border px-2 py-1.5 text-sm"
        style={{
          borderColor: 'var(--border)',
          backgroundColor: 'var(--background)',
          color: 'var(--foreground)',
        }}
      />

      <div className="max-h-48 overflow-y-auto" role="listbox" aria-label={t('simulation.selectClient')}>
        {isLoading && (
          <div className="px-2 py-3 text-center text-xs" style={{ color: 'var(--foreground-muted)' }}>
            {t('common.loading')}
          </div>
        )}
        {isError && (
          <div className="px-2 py-3 text-center text-xs" style={{ color: 'var(--danger, #D64545)' }}>
            {t('common.error')}
          </div>
        )}
        {!isLoading && !isError && filteredClients.map((client) => (
          <button
            key={client.glnNumber}
            role="option"
            aria-selected={selectedGln === client.glnNumber}
            data-testid={`simulation-client-${client.glnNumber}`}
            onClick={() => {
              setSelectedGln(client.glnNumber);
              setSelectedName(client.name);
            }}
            className="flex w-full items-center rounded px-2 py-1.5 text-left text-sm transition-colors"
            style={{
              backgroundColor:
                selectedGln === client.glnNumber
                  ? 'var(--primary-light)'
                  : undefined,
              color:
                selectedGln === client.glnNumber
                  ? 'var(--primary)'
                  : 'var(--foreground)',
            }}
          >
            <div className="min-w-0 flex-1">
              <div className="truncate font-medium">{client.name}</div>
              <div className="text-xs" style={{ color: 'var(--foreground-muted)' }}>
                {client.glnNumber}
              </div>
            </div>
          </button>
        ))}
        {!isLoading && !isError && filteredClients.length === 0 && (
          <div className="px-2 py-3 text-center text-xs" style={{ color: 'var(--foreground-muted)' }}>
            {t('common.noResults')}
          </div>
        )}
      </div>

      <button
        data-testid="simulation-start"
        onClick={handleStart}
        disabled={!selectedGln}
        className="mt-2 w-full rounded px-3 py-1.5 text-sm font-medium text-white transition-colors disabled:opacity-40"
        style={{ backgroundColor: 'var(--primary)' }}
      >
        {t('simulation.simulate')}
      </button>
    </div>
  );
}
